import React, { FC, KeyboardEvent, useState, useCallback } from "react";
import { Box } from "@material-ui/core";
import { useToastNotifications } from "../../utils/toastUtils";
import Layout from "../Common/NewLayout";
import Header from "../Common/NewLayout/Header";
import OfferList from "./OfferList";
import { OffersInfo } from "../../graphql/onboarding/offer/types/OffersInfo";
import { ConfigOfferInterface } from "../StepTwo/stepTwoLogic";
import { useMutation, useQuery } from "@apollo/client";
import { SIGNUP_PROGRESSION } from "../../graphql/account/mutation";
import { signupProgression } from "../../enums/account";
import {
  SignupProgression,
  SignupProgressionVariables,
} from "../../graphql/account/types/SignupProgression";
import { Navigate, useNavigate } from "react-router-dom";
import { AutoCompletion } from "../../graphql/onboarding/onboarding/types/AutoCompletion";
import { AUTO_COMPLETION } from "../../graphql/onboarding/onboarding/query";
import Condition from "./Condition";
import { OfferChoise } from "./logic";
import { CustomButton } from "../Ui/CustomButton";
import useStyles from "./style";
import {
  SelectOfferAndCompanyType,
  SelectOfferAndCompanyTypeVariables,
} from "../../graphql/onboarding/offer/types/SelectOfferAndCompanyType";
import { SELECT_OFFER_AND_COMPANY_TYPE } from "../../graphql/onboarding/offer/mutation";

interface OfferProps {
  offerDefault: OffersInfo | null;
  offers: (OffersInfo | null)[];
  config: ConfigOfferInterface;
  accepted?: boolean;
  create?: boolean;
}

const Offer: FC<OfferProps> = ({
  config,
  offerDefault,
  offers,
  accepted,
  create,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorToast } = useToastNotifications();

  const [isAuto, setIsAuto] = useState(true);
  const [pack, setPack] = useState(6);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [choice, setChoice] = useState<OfferChoise>({
    offerId: offerDefault?.id,
    cgsSapheer: !!accepted,
    gtLinkcy: !!accepted,
  });

  const [finishStep, { loading: finishStepLoading }] = useMutation<
    SignupProgression,
    SignupProgressionVariables
  >(SIGNUP_PROGRESSION);

  const { loading: autoLoading } = useQuery<AutoCompletion>(AUTO_COMPLETION, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => setIsAuto(!!data?.autoCompletion),
  });

  const [selectOffer, { loading: offerLoading, data: offerResult }] =
    useMutation<SelectOfferAndCompanyType, SelectOfferAndCompanyTypeVariables>(
      SELECT_OFFER_AND_COMPANY_TYPE,
      {
        onCompleted: () => {
          finishStep({ variables: { step: signupProgression.INFORMATION } });
          navigate(
            create
              ? "/explanation"
              : isAuto
              ? "/company"
              : "/companyInfo/create",
            {
              replace: true,
            },
          );
        },
        onError: (error) => {
          let message = "other.error";
          if (error.graphQLErrors) message = "server.error";
          else if (error.networkError) message = "network.error";
          showErrorToast({ description: message });
        },
      },
    );

  const handleNext = useCallback(async () => {
    const { offerId, cgsSapheer, gtLinkcy } = choice;

    if (!cgsSapheer || !gtLinkcy) {
      setIsSubmitted(true);
      showErrorToast({ description: "stepTwo.noCondition" });
      return;
    }

    if (!offerId) {
      showErrorToast({ description: "stepTwo.noOfferId" });
      return;
    }

    if (create && !pack) {
      showErrorToast({
        description: "Veuillez sélectionner le package qui vous convient.",
      });
      return;
    }

    try {
      await selectOffer({
        variables: {
          offerId,
          existingCompany: !create,
          packageSelected: create ? pack : null,
        },
      });
    } catch {
      // L'erreur sera déjà gérée dans onError, on affiche néanmoins un message générique.
      showErrorToast({ description: "stepTwo.selectOfferError" });
    }
  }, [choice, selectOffer, showErrorToast, isAuto, navigate]);

  const handlePrevious = () => {
    navigate("/choise", { replace: true });
  };

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleNext();
      }
    },
    [handleNext],
  );

  if (offerResult) {
    return <Navigate to={isAuto ? "/company" : "/companyInfo/create"} />;
  }

  return (
    <Layout
      title="Offres"
      description="Parcourez nos offres personnalisées et choisissez celle qui répond parfaitement à vos besoins. Des solutions flexibles pour vous accompagner au quotidien."
    >
      <Header
        title="Choisissez votre offre"
        description="Choisissez une offre adaptée à vos besoins."
      />
      <Box marginTop={2} onKeyPress={handleKeyPress}>
        <OfferList
          config={config}
          offerSelected={offerDefault}
          offers={offers}
          choice={choice}
          setChoice={setChoice}
          create={create}
          pack={{ packageSelected: pack, setPackageSelected: setPack }}
        />
        <Condition
          choice={choice}
          isSubmitted={isSubmitted}
          setChoice={setChoice}
        />
        <Box className={classes.button}>
          <CustomButton
            type="button"
            variant="outline4"
            onClick={handlePrevious}
            label="button.previous"
            disabled={finishStepLoading || offerLoading || autoLoading}
            fullWidthMobile
          />
          <CustomButton
            type="button"
            variant="primary"
            onClick={handleNext}
            label="button.next"
            loading={finishStepLoading || offerLoading || autoLoading}
            fullWidthMobile
          />
        </Box>
      </Box>
    </Layout>
  );
};

export default Offer;
